import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { RadioGroup, RadioTile, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { ServiceGemakFlowBoilerBrandStepRendering } from '@sitecore/types/ServiceGemakFlowBoilerBrandStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextServiceGemak } from '../../context';

interface FormFields {
  kcMerk: string;
}

export const BoilerBrandStep: FC = () => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowContextServiceGemak>();
  const sendToFlowMachine = useFlowActorRef().send;

  const kcMerk = useFlowSelector(state => state.context.kcMerk);

  const { ServiceGemakFlowBoilerBrandStep: { fields: textualData } = {} } = usePlaceholderContent<{
    ServiceGemakFlowBoilerBrandStep: ServiceGemakFlowBoilerBrandStepRendering;
  }>();

  const formSchema = yup.object({
    kcMerk: yup.string().required(textualData?.choice?.requiredMessageText?.value),
  });

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      kcMerk,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          kcMerk: formData.kcMerk,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <RichText html={textualData.content.text.value} />}
      </Header>

      <Controller
        control={control}
        name="kcMerk"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby="kcMerk"
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors?.kcMerk?.message}>
            {textualData?.choice?.brandsList?.map(({ name, fields }) => {
              const imageProps = fields.manufacturer?.brandImage.value;
              return (
                <RadioTile key={name} value={name}>
                  <RadioTile.Image
                    {...imageProps}
                    src={imageProps?.src || ''}
                    alt={imageProps?.alt || fields.manufacturer?.brandName.value || ''}
                  />
                </RadioTile>
              );
            })}
          </RadioGroup>
        )}
      />

      <TrackedDialog
        title={textualData?.dialogs?.brandUnknownDialog?.value?.title}
        trigger={<TextLink emphasis="high">{textualData?.dialogs?.brandUnknownDialog?.value?.triggerText}</TextLink>}>
        <RichText html={textualData?.dialogs?.brandUnknownDialog?.value?.content} />
      </TrackedDialog>

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
